import { CircularProgress } from '@mui/material'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import { Formik, Form } from 'formik'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { uploadFile } from 'api/file'
import { useUpdateProjectMutation } from 'apiv2'
import { prepareCommonSelects } from 'helpers/prepares'
import { useProjectPermissions } from 'hooks/useProjectPermissions'
import { getProjectListAction, getProjectUsersAction } from 'store/projects/'
import Button from 'ui/components/buttons/Button/'
import ImageUploader from 'ui/components/forms/ImageUploader/'
import Switch from 'ui/components/forms/Switch'
import Icon from 'ui/components/Icon'
import { Link } from 'ui/components/Link'
import { notifyError, notifySuccess } from 'ui/components/Notify/'
import Scrollbar from 'ui/components/Scrollbar/'
import SidebarModal from 'ui/components/SidebarModal/'
import SidebarModalLabel from 'ui/components/SidebarModalLabel/'
import UserRoleWidget from 'ui/components/UserRoleWidget/'
import {
  AddHeader,
  AddBody,
  AddInner,
  AddButtonGroup,
} from 'ui/modules/Projects/components/ProjectAdd/styled'
import { LoadingContainer, ImageUploaderTitle, FormGrid } from './styled'
import { useProjectDictionaries } from '../../hooks'
import { HeaderWrapper } from '../../styled'
import {
  ProjectBaseInfo,
  ProjectTypeField,
  ProjectPermUser,
  ProjectRatio,
  ProjectOrganizationField,
} from '../shared'
import { validationSchema } from '../utils'

const ProjectEdit = (props) => {
  const { closeModal, data, onCloseHandler, onProjectArchiveStatusChange } =
    props
  const dispatch = useDispatch()
  const [fileId, setFileId] = useState(null)
  const [fileImage, setFileImage] = useState(null)

  const {
    projectInfo,
    isProjectInfoLoading: projectInfoLoading,
    isAdmin,
    canEditProject,
    canEditProjectPerms,
  } = useProjectPermissions(data?.slug, {
    refetchOnMountOrArgChange: true,
  })

  const {
    projectTypeOptions,
    flowOptions,
    passboltOptions,
    projectPropertiesOptions,
    organizations,
    isProjectDictionariesLoading,
  } = useProjectDictionaries()

  const [updateProject, { isLoading }] = useUpdateProjectMutation()

  const isProjectArchived = !!projectInfo?.is_archived
  const canEditProjectSettings = !isProjectArchived || canEditProject
  const currentFlow = projectInfo?.flow
    ? prepareCommonSelects([projectInfo?.flow])[0]
    : ''
  const currentProjectType = projectInfo?.project_type
    ? prepareCommonSelects([projectInfo?.project_type])[0]
    : ''
  const currentProjectProp = projectInfo?.project_scope_type
    ? prepareCommonSelects([projectInfo?.project_scope_type])[0]
    : ''
  const currentPassboltOption = projectInfo?.passbolt_folder_id
    ? passboltOptions?.filter(
        (item) => item.id === projectInfo?.passbolt_folder_id
      )?.[0]
    : ''

  const canShowPermSection =
    canEditProjectPerms || isAdmin || !canEditProjectSettings

  const getCurrentOrganizationOption = (organizations, organizationId) => {
    if (!organizationId) {
      return null
    }

    return (
      organizations?.find(
        (organization) => organization.id === organizationId
      ) || null
    )
  }

  const initialValues = {
    name: projectInfo?.name || '',
    slug: projectInfo?.slug || '',
    wiki_link: projectInfo?.wiki_link || '',
    workflow: currentFlow,
    passbolt_folder: currentPassboltOption,
    project_type: currentProjectType,
    project_properties: currentProjectProp,
    begin: projectInfo?.begin
      ? new Date(projectInfo?.begin).toLocaleDateString('ru-RU')
      : null,
    end: projectInfo?.end
      ? new Date(projectInfo?.end).toLocaleDateString('ru-RU')
      : null,
    test_percentage: projectInfo?.test_percentage || '',
    management_percentage: projectInfo?.management_percentage || '',
    is_archived: projectInfo?.is_archived || false,
    perm_user_self_assign: projectInfo?.perm_user_self_assign
      ? projectInfo?.perm_user_self_assign
      : false,
    perm_user_create_task: projectInfo?.perm_user_create_task
      ? projectInfo?.perm_user_create_task
      : false,
    perm_manager_is_admin: projectInfo?.perm_manager_is_admin
      ? projectInfo?.perm_manager_is_admin
      : false,
    perm_user_to_rft: projectInfo?.perm_user_to_rft
      ? projectInfo?.perm_user_to_rft
      : false,
    perm_manager_create_supertask: !!projectInfo?.perm_manager_create_supertask
      ? !!projectInfo?.perm_manager_create_supertask
      : false,
    perm_epic_required: projectInfo?.perm_epic_required
      ? projectInfo?.perm_epic_required
      : false,
    perm_user_self_estimate: projectInfo?.perm_user_self_estimate
      ? projectInfo?.perm_user_self_estimate
      : false,
    perm_manager_edit_epic: projectInfo?.perm_manager_edit_epic
      ? projectInfo?.perm_manager_edit_epic
      : false,
    is_tech_support: projectInfo?.is_tech_support
      ? projectInfo?.is_tech_support
      : false,
    is_responsible_estimate_required:
      projectInfo?.is_responsible_estimate_required
        ? projectInfo?.is_responsible_estimate_required
        : false,
    organization_id: getCurrentOrganizationOption(
      organizations,
      projectInfo?.organization_id
    ),
  }

  const uploadImage = (file) => {
    const fd = new FormData()

    fd.append('file[]', file)

    uploadFile(fd).then(({ data }) => {
      setFileId(data[0].id)
    })
  }

  const sendForm = async (values) => {
    const isArchiveStatusChanged =
      !!projectInfo?.is_archived !== values?.is_archived

    updateProject({
      slug: projectInfo.slug,
      data: {
        ...values,
        passbolt_folder_id: values?.passbolt_folder?.id,
        workflow: values?.workflow.id,
        project_type_id: values?.project_type.id,
        project_scope_type_id: values?.project_properties.id,
        logo_id: fileId || projectInfo?.logo?.id,
        organization_id: values?.organization_id?.id ?? null,
      },
    })
      .unwrap()
      .then(() => {
        notifySuccess('Проект успешно изменен')

        if (isArchiveStatusChanged) {
          onProjectArchiveStatusChange?.(projectInfo?.id)
        }
      })
      .catch(() => notifyError('Произошла ошибка'))
      .finally(() => {
        dispatch(getProjectUsersAction(projectInfo?.slug))
        dispatch(getProjectListAction())
        closeModal()
      })
  }

  if (projectInfoLoading || isProjectDictionariesLoading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    )
  }

  return (
    <Scrollbar maxHeight="100vh">
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          sendForm(values)
        }}
      >
        {(formik) => (
          <Form onSubmit={formik.handleSubmit} noValidate>
            <HeaderWrapper>
              <AddHeader title="Добавить проект" {...props} />
              <div onClick={() => closeModal()}>
                <Icon name="close" />
              </div>
            </HeaderWrapper>
            <AddBody>
              <SidebarModalLabel title="Общая информация" />
              <AddInner style={{ paddingTop: '24px' }}>
                <ImageUploader
                  required={true}
                  label={
                    <ImageUploaderTitle>
                      <span>Логотип проекта</span>
                      {projectInfo?.deal_link && (
                        <Link
                          href={`${projectInfo?.deal_link}`}
                          target="_blank"
                        >
                          Ссылка на сделку в Битрикс24
                        </Link>
                      )}
                    </ImageUploaderTitle>
                  }
                  disabled={!canEditProjectSettings}
                  fileImage={
                    fileImage
                      ? fileImage
                      : [
                          {
                            dataUrl: projectInfo?.logo?.link,
                            isApi: projectInfo?.logo?.is_api,
                          },
                        ]
                  }
                  onUpload={(image) => {
                    uploadImage(image[0].file)
                    setFileImage(image)
                  }}
                />
                <FormGrid>
                  <ProjectBaseInfo
                    flowOptions={flowOptions}
                    projectPropertiesOptions={projectPropertiesOptions}
                    passboltOptions={passboltOptions}
                    canEditProjectSettings={canEditProjectSettings}
                  />
                  <ProjectTypeField
                    projectTypeOptions={projectTypeOptions}
                    canEditProjectSettings={canEditProjectSettings}
                  />
                  <ProjectRatio
                    canEditProjectSettings={canEditProjectSettings}
                  />
                  <ProjectOrganizationField
                    organizations={organizations}
                    canEditProjectSettings={canEditProjectSettings}
                  />
                </FormGrid>
                {canShowPermSection && (
                  <>
                    <Switch
                      label="Архивный проект"
                      checked={formik.getFieldProps('is_archived').value}
                      onChange={() => {
                        formik.setFieldValue(
                          'is_archived',
                          !formik.getFieldProps('is_archived').value
                        )
                      }}
                      disabled={!canEditProjectSettings}
                    />
                    <ProjectPermUser
                      canEditProjectSettings={canEditProjectSettings}
                    />
                  </>
                )}
                <UserRoleWidget
                  projectSlug={data?.slug}
                  onCloseHandler={onCloseHandler}
                  users={projectInfo?.users}
                  canEditProjectSettings={canEditProjectSettings}
                />
              </AddInner>
            </AddBody>
            <AddButtonGroup>
              <AddInner>
                {canEditProjectSettings && (
                  <Button loading={isLoading} title="Сохранить" type="submit" />
                )}
                <Button
                  title="Отменить"
                  theme="borderGrey"
                  type="reset"
                  onClick={closeModal}
                />
              </AddInner>
            </AddButtonGroup>
          </Form>
        )}
      </Formik>
    </Scrollbar>
  )
}

export const ProjectEditModal = ({
  headerTitle,
  isOpened,
  fromList,
  data,
  onCloseHandler,
  onProjectArchiveStatusChange,
  trigger,
  triggerStyle,
}) => (
  <SidebarModal
    isOpened={isOpened}
    onCloseHandler={onCloseHandler}
    trigger={trigger}
    triggerStyle={triggerStyle}
  >
    {isOpened && (
      <ProjectEdit
        fromList={fromList}
        data={data}
        title={headerTitle}
        onCloseHandler={onCloseHandler}
        onProjectArchiveStatusChange={onProjectArchiveStatusChange}
      />
    )}
  </SidebarModal>
)

export const ProjectEditDrawer = (props) => {
  return (
    <SwipeableDrawer anchor="bottom" {...props}>
      {props.isOpened && (
        <ProjectEdit
          fromList={props.fromList}
          data={props.data}
          title={props.headerTitle}
          {...props}
        />
      )}
    </SwipeableDrawer>
  )
}
